import { createBrowserRouter, Navigate, redirect, type RouteObject } from "react-router";
import { RouteErrorFallback } from "@/components/ErrorBoundary";
import EhrPage from "@/pages/EhrPage";
import InsightDetailPage from "@/pages/InsightDetailPage";
import InsightsListPage from "@/pages/InsightsListPage";
import RateCheckPage from "@/pages/RateCheckPage";

type RouteComponent = NonNullable<RouteObject["Component"]>;

const lazyDefault = (load: () => Promise<{ default: RouteComponent }>) => () =>
  load().then((m) => ({ Component: m.default }));

export const router = createBrowserRouter([
  {
    path: "/",
    lazy: lazyDefault(() => import("@/pages/LandingPage")),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/terms",
    lazy: lazyDefault(() => import("@/pages/TermsPage")),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/privacy",
    lazy: lazyDefault(() => import("@/pages/PrivacyPage")),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/hipaa",
    lazy: lazyDefault(() => import("@/pages/HipaaPage")),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/about",
    lazy: lazyDefault(() => import("@/pages/AboutPage")),
    errorElement: <RouteErrorFallback />,
  },
  // The waitlist pages are not lazy: a lazy route renders nothing until its
  // chunk arrives, which blanks the prerendered page on a phone mid-load.
  {
    path: "/ehr",
    Component: EhrPage,
    errorElement: <RouteErrorFallback />,
  },
  // The page's first address, still in the earliest ads. The server answers
  // it with public/free-ehr.html, which forwards; this covers an in-app
  // navigation. Both keep the ad's UTM tags.
  {
    path: "/free-ehr",
    loader: ({ request }) => redirect(`/ehr${new URL(request.url).search}`),
  },
  {
    path: "/rate-check",
    Component: RateCheckPage,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/news",
    Component: InsightsListPage,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/news/:date",
    Component: InsightDetailPage,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/news/:date/:slug",
    Component: InsightDetailPage,
    errorElement: <RouteErrorFallback />,
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

// ---------------------------------------------------------------------------
// Scroll-to-top on new navigations.
// ---------------------------------------------------------------------------
let previousPathname = window.location.pathname;

router.subscribe((state) => {
  if (state.navigation.state !== "idle") return;
  if (state.historyAction !== "PUSH") return;
  const nextPathname = state.location.pathname;
  if (nextPathname === previousPathname) return;
  previousPathname = nextPathname;
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
});
